import { Box, Grid, Typography, Tooltip } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { DataGrid, GridCellParams, GridColDef } from '@mui/x-data-grid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { ptBR } from '@mui/material/locale';
import { useState, useEffect } from 'react';
import { useTranslation } from '../../../../../commons/useTranslasion';
import { IComanda, StatusReuniaoEnum, StatusValidacaoEnum } from '../../../../../commons/types/crm';

interface IEtapaComponentProps {
    comanda?: IComanda;
    loading: boolean;
}

interface IRowEtapa {
    id: number;
    nomeEtapa: string;
    statusAprovados: number;
    statusNoShow: number;
    totalReunioes: number;
}

const EtapaComponent = ({ comanda, loading }: IEtapaComponentProps) => {
    const [rows, setRows] = useState<IRowEtapa[]>([]);
    const { translate } = useTranslation();
    const locale = ptBR;

    useEffect(() => {
        if (comanda?.etapas && comanda?.comandaEtapaReunioes) {
            const etapasRows = comanda.etapas.map((etapa) => {
                // Filtra as reuniões desta etapa
                const reunioesEtapa = comanda.comandaEtapaReunioes?.filter(
                    (reuniao) => reuniao.tbEtapaId === etapa.id
                ) || [];

                // Conta as reuniões aprovadas
                const reunioesAprovadas = reunioesEtapa.filter((reuniao) => {
                    const statusAprovadoConsultor = String(reuniao.status) === String(StatusReuniaoEnum.PENDENTE_APROVACAO);
                    const statusReuniaoAprovado = String(reuniao.status) === String(StatusReuniaoEnum.APROVADA);
                    const statusAprovado = String(reuniao.statusValidacao) === String(StatusValidacaoEnum.APROVADO);
                    return statusAprovadoConsultor || statusReuniaoAprovado || statusAprovado;
                });

                // Conta as reuniões com no_show
                const reunioesNoShow = reunioesEtapa.filter((reuniao) => {
                    return String(reuniao.status) === String(StatusReuniaoEnum.NO_SHOW);
                });

                return {
                    id: etapa.id || 0,
                    nomeEtapa: etapa.nome || "",
                    statusAprovados: reunioesAprovadas.length,
                    statusNoShow: reunioesNoShow.length,
                    totalReunioes: reunioesEtapa.length
                };
            });

            setRows(etapasRows);
        }
    }, [comanda]);

    // Definição das colunas
    const columns: GridColDef[] = [
        {
            field: 'nomeEtapa',
            headerName: translate('etapa'),
            flex: 1,
            minWidth: 200,
        },
        {
            field: 'status',
            headerName: translate('status'),
            flex: 1,
            minWidth: 150,
            sortable: false,
            renderCell: (params: GridCellParams) => {
                const row = params.row as IRowEtapa;
                return (
                    <Box sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 0.5, 
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        height: '100%'
                    }}>
                        {Array.from({ length: row.statusAprovados }).map((_, idx) => (
                            <Tooltip key={`approved-${idx}`} title={translate('aprovada')}>
                                <CheckCircleIcon color="success" fontSize="small" />
                            </Tooltip>
                        ))}

                        {Array.from({ length: row.statusNoShow }).map((_, idx) => (
                            <Tooltip key={`noshow-${idx}`} title={translate('naoCompareceu')}>
                                <CancelIcon color="error" fontSize="small" />
                            </Tooltip>
                        ))}
                    </Box>
                );
            },
        },
        {
            field: 'totalReunioes',
            headerName: translate('totalReunioes'),
            type: 'number',
            width: 150,
            align: 'center',
            headerAlign: 'center',
        }
    ];

    return (
        <>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
                {translate('etapas')}
            </Typography>
            <Box sx={{ width: '100%' }}>
                <ThemeProvider theme={createTheme({}, locale)}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        loading={loading}
                        disableRowSelectionOnClick
                        hideFooterPagination={true}
                        hideFooter={rows.length <= 10}
                        autoHeight={rows.length <= 10}
                        density="compact"
                        getRowId={(row) => row.id}
                    />
                </ThemeProvider>
            </Box>
        </>
    );
};

export default EtapaComponent;
